import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService, documentService, loanService } from '../../services/loanService';
import { LoanStatusBadge } from '../../components/common/StatusBadge';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';

const ALL_DOC_TYPES = [
  { value: 'bank_statement',    label: 'Bank Statement' },
  { value: 'id_front',          label: 'ID Card (Front)' },
  { value: 'id_back',           label: 'ID Card (Back)' },
  { value: 'proof_of_address',  label: 'Proof of Address' },
  { value: 'pay_slip',          label: 'Pay Slip' },
  { value: 'tax_return',        label: 'Tax Return' },
  { value: 'business_reg',      label: 'Business Registration' },
  { value: 'other',             label: 'Other' },
];

const DOC_STATUS_META = {
  pending:           { label: 'Pending Review',    color: '#ff9900' },
  approved:          { label: 'Approved',           color: '#00C896' },
  rejected:          { label: 'Rejected',           color: '#FF4757' },
  resubmit_required: { label: 'Resubmit Required', color: '#818CF8' },
};

const REQUEST_STATUS_META = {
  pending:   { label: 'Awaiting Upload', color: '#ff9900' },
  fulfilled: { label: 'Uploaded',        color: '#5B8EFF' },
  waived:    { label: 'Waived',          color: '#94a3b8' },
};

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fc = (n) => `₦${parseFloat(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 0 })}`;

function RecommendationBadge({ value }) {
  const meta = {
    good:   { label: 'GOOD',              color: '#00C896', bg: 'rgba(0,200,150,0.12)' },
    bad:    { label: 'BAD — Review Risk', color: '#FF4757', bg: 'rgba(255,71,87,0.12)' },
    no_hit: { label: 'NO CREDIT HISTORY', color: '#ff9900', bg: 'rgba(255,153,0,0.12)' },
  }[value] || { label: value?.toUpperCase(), color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' };

  return (
    <span style={{
      fontWeight: 700, fontSize: '0.78rem', padding: '0.3rem 1rem',
      borderRadius: 99, letterSpacing: '0.06em',
      background: meta.bg, color: meta.color, border: `1px solid ${meta.color}40`,
    }}>{meta.label}</span>
  );
}

function NanoSummary({ summary }) {
  return (
    <div>
      <div className="table-wrapper" style={{ marginBottom: '0.75rem' }}>
        <table style={{ fontSize: '0.8rem' }}>
          <thead>
            <tr>
              <th>Bank Group</th>
              <th>Has Credit Facilities</th>
              <th>Delinquent Accounts</th>
              <th>Last Reported</th>
            </tr>
          </thead>
          <tbody>
            {(summary.sections || []).map(s => (
              <tr key={s.label} style={s.delinquent_facilities > 0 ? { background: 'rgba(255,71,87,0.04)' } : {}}>
                <td style={{ fontWeight: 500 }}>{s.label}</td>
                <td>
                  <span style={{ color: s.has_credit_facilities === 'YES' ? 'var(--primary)' : 'var(--text-3)' }}>
                    {s.has_credit_facilities}
                  </span>
                </td>
                <td>
                  <span style={{ fontWeight: 700, color: s.delinquent_facilities > 0 ? '#FF4757' : '#00C896' }}>
                    {s.delinquent_facilities}
                  </span>
                </td>
                <td style={{ color: 'var(--text-3)' }}>{s.last_reported_date || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {summary.nonperforming_sections?.length > 0 && (
        <div style={{ fontSize: '0.82rem', color: '#FF4757', background: 'rgba(255,71,87,0.06)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: 6, padding: '0.6rem 0.875rem' }}>
          <strong>Non-performing in:</strong> {summary.nonperforming_sections.join(', ')}
        </div>
      )}
    </div>
  );
}

function ClassicSummary({ summary }) {
  const fmt = (n) => n?.toLocaleString('en-NG', { minimumFractionDigits: 0 }) ?? '0';
  const t = summary.totals || {};
  return (
    <div>
      <div className="table-wrapper" style={{ marginBottom: '0.75rem' }}>
        <table style={{ fontSize: '0.78rem' }}>
          <thead>
            <tr>
              <th>Institution</th>
              <th>Facilities</th>
              <th>Approved (₦)</th>
              <th>Balance (₦)</th>
              <th>Overdue (₦)</th>
              <th>Non-Perf.</th>
              <th>Performing</th>
            </tr>
          </thead>
          <tbody>
            {(summary.institutions || []).map((inst, i) => (
              <tr key={i} style={(inst.overdue_amount > 0 || inst.nonperforming_facility > 0) ? { background: 'rgba(255,71,87,0.04)' } : {}}>
                <td style={{ maxWidth: 200 }}>
                  <div style={{ fontWeight: 500, lineHeight: 1.3 }}>{inst.name}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>{inst.data_provider}</div>
                </td>
                <td>{inst.facilities_count}</td>
                <td>{fmt(inst.approved_amount)}</td>
                <td>{fmt(inst.account_balance)}</td>
                <td style={{ color: inst.overdue_amount > 0 ? '#FF4757' : 'inherit', fontWeight: inst.overdue_amount > 0 ? 700 : 400 }}>
                  {fmt(inst.overdue_amount)}
                </td>
                <td style={{ color: inst.nonperforming_facility > 0 ? '#FF4757' : 'inherit', fontWeight: inst.nonperforming_facility > 0 ? 700 : 400 }}>
                  {inst.nonperforming_facility}
                </td>
                <td style={{ color: '#00C896' }}>{inst.performing_facility}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--surface-alt)', fontWeight: 700 }}>
              <td>TOTALS</td>
              <td>{t.facilities_count ?? '—'}</td>
              <td>{fmt(t.approved_amount)}</td>
              <td>{fmt(t.account_balance)}</td>
              <td style={{ color: (t.overdue_amount || 0) > 0 ? '#FF4757' : 'inherit' }}>{fmt(t.overdue_amount)}</td>
              <td style={{ color: (t.nonperforming_facility || 0) > 0 ? '#FF4757' : 'inherit' }}>{t.nonperforming_facility ?? '—'}</td>
              <td style={{ color: '#00C896' }}>{t.performing_facility ?? '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {summary.problem_institutions?.length > 0 && (
        <div style={{ fontSize: '0.82rem', color: '#FF4757', background: 'rgba(255,71,87,0.06)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: 6, padding: '0.6rem 0.875rem' }}>
          <strong>Overdue / non-performing at:</strong>{' '}
          {summary.problem_institutions.map(p => p.name).join(', ')}
        </div>
      )}
    </div>
  );
}

// ── Document request row ──────────────────────────────────────────────────────

function DocumentRequestRow({ req, reviewDocMutation, waiveMutation }) {
  const [resubmitReason, setResubmitReason] = useState('');
  const [showResubmit, setShowResubmit] = useState(false);

  const rMeta = REQUEST_STATUS_META[req.status] || { label: req.status, color: '#94a3b8' };
  const doc = req.fulfilled_document_id ? req : null;
  const dMeta = doc ? (DOC_STATUS_META[req.fulfilled_document_status] || { label: req.fulfilled_document_status, color: '#94a3b8' }) : null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '0.75rem 1rem', background: 'var(--surface-alt)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{req.doc_type_display}</span>
          <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 99, background: `${rMeta.color}18`, color: rMeta.color, border: `1px solid ${rMeta.color}30` }}>
            {rMeta.label}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {req.status === 'pending' && (
            <button
              className="btn btn-ghost"
              style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem' }}
              disabled={waiveMutation.isPending}
              onClick={() => waiveMutation.mutate(req.id)}
            >
              Waive
            </button>
          )}
        </div>
      </div>

      {req.notes && (
        <div style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: 'var(--text-2)', borderBottom: '1px solid var(--border)' }}>
          <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>Instructions: </span>{req.notes}
        </div>
      )}

      {doc && (
        <div style={{ padding: '0.75rem 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 99, background: `${dMeta.color}18`, color: dMeta.color, border: `1px solid ${dMeta.color}30` }}>
                {dMeta.label}
              </span>
              {req.fulfilled_document_file && (
                <a href={req.fulfilled_document_file} target="_blank" rel="noreferrer"
                  style={{ fontSize: '0.8rem', color: 'var(--primary)', textDecoration: 'none' }}>
                  View Document ↗
                </a>
              )}
            </div>
            {req.fulfilled_document_status === 'pending' && (
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem', background: '#1596E0', border: 'none' }}
                  disabled={reviewDocMutation.isPending}
                  onClick={() => reviewDocMutation.mutate({ docId: req.fulfilled_document_id, status: 'approved' })}
                >
                  Approve
                </button>
                <button
                  className="btn btn-ghost"
                  style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem', color: '#818CF8', borderColor: '#818CF840' }}
                  onClick={() => setShowResubmit(v => !v)}
                >
                  Request Resubmit
                </button>
                <button
                  className="btn btn-ghost"
                  style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem', color: '#FF4757', borderColor: '#FF475740' }}
                  disabled={reviewDocMutation.isPending}
                  onClick={() => { const r = window.prompt('Rejection reason:'); if (r) reviewDocMutation.mutate({ docId: req.fulfilled_document_id, status: 'rejected', rejection_reason: r }); }}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
          {req.fulfilled_document_rejection_reason && (
            <p style={{ fontSize: '0.78rem', color: '#FF4757', marginTop: '0.4rem' }}>
              Reason: {req.fulfilled_document_rejection_reason}
            </p>
          )}
          {showResubmit && (
            <div style={{ marginTop: '0.625rem', display: 'flex', gap: '0.5rem' }}>
              <input
                className="form-input"
                placeholder="Reason for resubmission…"
                value={resubmitReason}
                onChange={e => setResubmitReason(e.target.value)}
                style={{ fontSize: '0.8rem', flex: 1 }}
              />
              <button
                className="btn btn-primary"
                style={{ fontSize: '0.72rem', padding: '0.3rem 0.75rem', background: '#818CF8', border: 'none', whiteSpace: 'nowrap' }}
                disabled={reviewDocMutation.isPending || !resubmitReason.trim()}
                onClick={() => {
                  reviewDocMutation.mutate({ docId: req.fulfilled_document_id, status: 'resubmit_required', rejection_reason: resubmitReason });
                  setShowResubmit(false);
                  setResubmitReason('');
                }}
              >
                Send
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Reusable info row ─────────────────────────────────────────────────────────

function InfoRow({ label, value, color, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.375rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.82rem', gap: '0.5rem' }}>
      <span style={{ color: 'var(--text-3)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 500, color: color || 'var(--text-1)', textAlign: 'right', fontFamily: mono ? 'monospace' : undefined }}>
        {value || '—'}
      </span>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, subtitle, badge, children, style }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: '1.25rem', ...style }}>
      <div style={{
        padding: '0.75rem 1.125rem',
        background: 'var(--surface-alt)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>{title}</span>
          {subtitle && <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginLeft: '0.5rem' }}>{subtitle}</span>}
        </div>
        {badge}
      </div>
      <div style={{ padding: '1.125rem' }}>{children}</div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LoanApplicationReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMaker, isChecker } = useAuth();
  const canReview = isMaker || isChecker;
  const qc = useQueryClient();

  // ── Loan detail ─────────────────────────────────────────────────────────────
  const { data: loan, isLoading, error } = useQuery({
    queryKey: ['admin-loan-detail', id],
    queryFn: () => adminService.getAdminLoan(id).then(r => r.data.data),
  });

  // ── Bureau check state ──────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('nano');
  const [checkErr, setCheckErr] = useState('');

  const creditCheck = loan?.credit_check || null;
  const nanoResult = creditCheck?.nano_result || null;
  const classicResult = creditCheck?.classic_result || null;

  const runCheck = useMutation({
    mutationFn: () => adminService.runBureauCreditCheck(id, activeTab),
    onSuccess: () => {
      setCheckErr('');
      qc.invalidateQueries({ queryKey: ['admin-loan-detail', id] });
    },
    onError: (e) => setCheckErr(e.response?.data?.message || 'Credit check failed. Please try again.'),
  });

  // ── Maker action state ──────────────────────────────────────────────────────
  const [action, setAction] = useState('under_review');
  const [amount, setAmount] = useState('');
  const [rate, setRate] = useState('18');
  const [tenure, setTenure] = useState('');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [actionResult, setActionResult] = useState(null);
  const [actionSuccessMsg, setActionSuccessMsg] = useState('');

  useEffect(() => {
    if (loan) {
      setAmount(loan.approved_amount || loan.requested_amount || '');
      setRate(loan.applied_interest_rate || '18');
      setTenure(loan.requested_tenure_months || '');
    }
  }, [loan?.id]);

  // ── Live schedule preview ───────────────────────────────────────────────────
  const [previewParams, setPreviewParams] = useState(null);
  useEffect(() => {
    if (action !== 'approve') { setPreviewParams(null); return; }
    const t = setTimeout(() => {
      const a = parseFloat(amount);
      const r = parseFloat(rate);
      const ten = parseInt(tenure);
      if (a > 0 && r > 0 && ten > 0) {
        setPreviewParams({
          amount: a,
          interest_rate: r,
          tenure_months: ten,
          payment_count: loan?.payment_count || 1,
          loan_product_id: loan?.loan_product || undefined,
        });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [action, amount, rate, tenure, loan?.payment_count, loan?.loan_product]);

  const { data: schedulePreview } = useQuery({
    queryKey: ['maker-schedule-preview', previewParams],
    queryFn: () => loanService.previewSchedule(previewParams).then(r => r.data.data),
    enabled: !!previewParams,
  });

  const actionMutation = useMutation({
    mutationFn: (d) => adminService.loanAction(id, d),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin-loan-detail', id] });
      qc.invalidateQueries({ queryKey: ['admin-loans'] });
      if (r.data.approval_ref) {
        setActionResult({ ref: r.data.approval_ref, msg: r.data.message });
      } else {
        setActionSuccessMsg(r.data.message || 'Action applied successfully.');
        setDocSelections({});
        setNotes('');
        refetchDocRequests();
      }
    },
    onError: (e) => setActionErr(e.response?.data?.message || 'Action failed. Please try again.'),
  });

  const handleSubmit = () => {
    setActionErr('');
    if (action === 'request_docs') {
      const selected = Object.keys(docSelections);
      if (selected.length === 0) { setActionErr('Select at least one document type to request.'); return; }
      actionMutation.mutate({
        action,
        notes,
        documents: selected.map(dt => ({ doc_type: dt, notes: docSelections[dt] })),
      });
      return;
    }
    const payload = { action, notes };
    if (action === 'approve') {
      payload.approved_amount = parseFloat(amount);
      payload.interest_rate = parseFloat(rate);
      payload.tenure_months = parseInt(tenure);
    }
    if (action === 'reject') payload.rejection_reason = rejectionReason;
    actionMutation.mutate(payload);
  };

  const needsCheck = ['approve', 'reject'].includes(action) && !nanoResult && !classicResult;

  // ── Document request state ──────────────────────────────────────────────────
  const [docSelections, setDocSelections] = useState({});
  const toggleDocType = (value) => setDocSelections(prev => {
    const next = { ...prev };
    if (next[value] !== undefined) delete next[value]; else next[value] = '';
    return next;
  });
  const setDocNote = (value, note) => setDocSelections(prev => ({ ...prev, [value]: note }));

  const { data: docRequestsData, refetch: refetchDocRequests } = useQuery({
    queryKey: ['loan-doc-requests', id],
    queryFn: () => documentService.getLoanDocumentRequests(id).then(r => r.data.data),
    enabled: !!id,
  });
  const docRequests = Array.isArray(docRequestsData) ? docRequestsData : (docRequestsData?.results || []);

  const reviewDocMutation = useMutation({
    mutationFn: ({ docId, status: s, rejection_reason }) =>
      documentService.reviewDocument(docId, { status: s, rejection_reason }),
    onSuccess: () => { refetchDocRequests(); qc.invalidateQueries({ queryKey: ['admin-loan-detail', id] }); },
  });

  const waiveMutation = useMutation({
    mutationFn: (reqId) => documentService.waiveRequest(reqId),
    onSuccess: () => refetchDocRequests(),
  });

  // ── Checker decision state ──────────────────────────────────────────────────
  const [checkerDecision, setCheckerDecision] = useState('approved');
  const [checkerComment, setCheckerComment] = useState('');
  const [checkerErr, setCheckerErr] = useState('');
  const [checkerDone, setCheckerDone] = useState(false);

  const { data: approvalData } = useQuery({
    queryKey: ['loan-pending-approval', id],
    queryFn: () => adminService.getApprovals({ target_id: id, module: 'loans' }).then(r => r.data.data),
    enabled: isChecker,
  });

  const pendingApproval = (approvalData?.results || approvalData || [])
    .find(a => a.status === 'pending' || a.status === 'under_review') || null;

  const decideMutation = useMutation({
    mutationFn: (d) => adminService.decideApproval(pendingApproval.id, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-approvals'] });
      qc.invalidateQueries({ queryKey: ['loan-pending-approval', id] });
      qc.invalidateQueries({ queryKey: ['admin-loan-detail', id] });
      setCheckerErr('');
      setCheckerDone(true);
    },
    onError: (e) => setCheckerErr(e.response?.data?.message || 'Decision failed. Please try again.'),
  });

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) return (
    <div style={{ padding: '3rem', display: 'flex', justifyContent: 'center' }}>
      <LoadingSpinner />
    </div>
  );

  if (error || !loan) return (
    <div style={{ padding: '3rem', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-2)', marginBottom: '1rem' }}>
        {error?.response?.data?.message || 'Application not found.'}
      </p>
      <button className="btn btn-ghost" onClick={() => navigate('/admin/applications')}>
        ← Back to Applications
      </button>
    </div>
  );

  const kycColor = (s) => s === 'approved' ? '#00C896' : s === 'rejected' ? '#FF4757' : s === 'pending' ? '#ff9900' : 'var(--text-2)';

  // ── Action panel (right column) content ─────────────────────────────────────

  const ActionPanel = () => {
    if (!isMaker) return null;

    if (['offer_accepted', 'disbursed', 'active', 'closed'].includes(loan.status)) {
      return (
        <Section title="Loan Action">
          <div className="alert alert-info">
            <span>ℹ</span>
            <span style={{ fontSize: '0.85rem' }}>The applicant has accepted the offer. This loan is now view-only.</span>
          </div>
        </Section>
      );
    }

    if (loan.status === 'offer_sent') {
      return (
        <Section title="Loan Action">
          <div className="alert alert-info">
            <span>ℹ</span>
            <span style={{ fontSize: '0.85rem' }}>Offer has been sent to the applicant and is awaiting acceptance.</span>
          </div>
        </Section>
      );
    }

    if (!['submitted', 'under_review', 'credit_check'].includes(loan.status)) {
      return (
        <Section title="Loan Action">
          <div className="alert alert-info">
            <span>ℹ</span>
            <span style={{ fontSize: '0.85rem' }}>This application can no longer be actioned.</span>
          </div>
        </Section>
      );
    }

    if (loan.maker_recommended_at) {
      return (
        <Section title="Loan Action">
          <div className="alert alert-info">
            <span>⚖</span>
            <span style={{ fontSize: '0.85rem' }}>
              Recommendation already submitted. Pending checker review.
            </span>
          </div>
        </Section>
      );
    }

    if (actionResult) {
      return (
        <Section title="Action Submitted">
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✓</div>
            <h2 style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: '0.375rem', fontSize: '1.05rem' }}>
              Sent for Checker Review
            </h2>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', marginBottom: '1rem' }}>{actionResult.msg}</p>
            <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.625rem 1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>
                Approval Reference
              </div>
              <div style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', fontSize: '0.95rem' }}>
                {actionResult.ref}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button className="btn btn-primary" style={{ fontSize: '0.82rem' }} onClick={() => navigate('/admin/approvals')}>
                View Approval Queue
              </button>
              <button className="btn btn-ghost" style={{ fontSize: '0.82rem' }} onClick={() => navigate('/admin/applications')}>
                Back to Applications
              </button>
            </div>
          </div>
        </Section>
      );
    }

    return (
      <Section title="Loan Action" subtitle="Approve / reject creates a maker-checker request">
        <div className="alert alert-info" style={{ marginBottom: '1rem', fontSize: '0.78rem' }}>
          <span>⚖</span>
          <span>Approve / Reject are sent for checker confirmation. Under Review and Request Docs take effect immediately.</span>
        </div>

        <div className="form-group">
          <label className="form-label">Action</label>
          <select className="form-input" value={action} onChange={e => { setAction(e.target.value); setActionErr(''); }}>
            <option value="under_review">Mark Under Review</option>
            <option value="request_docs">Request Additional Documents</option>
            <option value="approve">Recommend Approval</option>
            <option value="reject">Recommend Rejection</option>
          </select>
        </div>

        {needsCheck && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            <span>⚠</span>
            <span style={{ fontSize: '0.8rem' }}>
              Run a bureau credit check before you can {action} this application.
            </span>
          </div>
        )}

        {action === 'approve' && (
          <>
            {(() => {
              const requested = parseFloat(loan.requested_amount || 0);
              const current = parseFloat(amount || 0);
              const diff = requested - current;
              return (
                <div style={{
                  background: 'var(--surface-alt)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '0.5rem 0.75rem', marginBottom: '0.875rem',
                  fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
                }}>
                  <span style={{ color: 'var(--text-3)' }}>Requested: </span>
                  <strong>{fc(requested)}</strong>
                  {diff > 0.01 && (
                    <span style={{ color: '#ff9900', fontSize: '0.72rem', fontWeight: 600, background: 'rgba(255,153,0,0.08)', border: '1px solid rgba(255,153,0,0.2)', padding: '0.1rem 0.4rem', borderRadius: 99 }}>
                      ↓ Reduced by {fc(diff)}
                    </span>
                  )}
                  {diff < -0.01 && <span style={{ color: '#FF4757', fontSize: '0.72rem' }}>⚠ Exceeds requested</span>}
                  {Math.abs(diff) <= 0.01 && current > 0 && <span style={{ color: '#00C896', fontSize: '0.72rem' }}>Full amount</span>}
                </div>
              );
            })()}

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Approved Amount (₦)</label>
                <input className="form-input" type="number" value={amount} onChange={e => setAmount(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Rate (% p.m.)</label>
                <input className="form-input" type="number" step="0.1" value={rate} onChange={e => setRate(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Tenure (months)</label>
              <input className="form-input" type="number" value={tenure} onChange={e => setTenure(e.target.value)} />
            </div>

            {schedulePreview && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: '0.875rem' }}>
                <div style={{ padding: '0.4rem 0.75rem', background: 'var(--surface-alt)', borderBottom: '1px solid var(--border)', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Repayment Preview
                </div>
                <div style={{ padding: '0.625rem 0.75rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  {[
                    { label: 'Per Installment', value: fc(schedulePreview.installment_amount), color: 'var(--primary)' },
                    { label: 'Payments',         value: schedulePreview.payment_count },
                    { label: 'Total Interest',   value: fc(schedulePreview.total_interest) },
                    { label: 'Total Repayment',  value: fc(schedulePreview.total_repayment) },
                    { label: 'Processing Fee',   value: fc(schedulePreview.processing_fee) },
                    { label: 'Net to Disburse',  value: fc(schedulePreview.net_disbursement), color: '#00C896' },
                  ].map(({ label, value, color }) => (
                    <div key={label}>
                      <div style={{ fontSize: '0.67rem', color: 'var(--text-3)', marginBottom: 1 }}>{label}</div>
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: color || 'var(--text-1)' }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {action === 'reject' && (
          <div className="form-group">
            <label className="form-label">Rejection Reason <span style={{ color: '#FF4757' }}>*</span></label>
            <textarea className="form-input" rows={3} value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="Explain why this application is being rejected…" />
          </div>
        )}

        {action === 'request_docs' && (
          <div className="form-group">
            <label className="form-label">Documents to Request <span style={{ color: '#FF4757' }}>*</span></label>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginBottom: '0.625rem' }}>
              Check each type needed; optionally add instructions.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {ALL_DOC_TYPES.map(({ value, label }) => {
                const selected = docSelections[value] !== undefined;
                return (
                  <div key={value} style={{
                    border: `1px solid ${selected ? 'rgba(91,142,255,0.4)' : 'var(--border)'}`,
                    borderRadius: 8, overflow: 'hidden',
                    background: selected ? 'rgba(91,142,255,0.05)' : 'var(--surface-alt)',
                    transition: 'all .15s',
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selected} onChange={() => toggleDocType(value)}
                        style={{ accentColor: '#5B8EFF', width: 13, height: 13, flexShrink: 0 }} />
                      <span style={{ fontSize: '0.8rem', fontWeight: selected ? 600 : 400, color: selected ? '#5B8EFF' : 'var(--text-2)' }}>
                        {label}
                      </span>
                    </label>
                    {selected && (
                      <div style={{ padding: '0 0.75rem 0.5rem' }}>
                        <input className="form-input" placeholder={`Instructions for ${label} (optional)`}
                          value={docSelections[value]} onChange={e => setDocNote(value, e.target.value)}
                          style={{ fontSize: '0.78rem' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {action !== 'request_docs' && (
          <div className="form-group">
            <label className="form-label">Internal Notes</label>
            <textarea className="form-input" rows={2} value={notes}
              onChange={e => setNotes(e.target.value)} placeholder="Optional notes…" />
          </div>
        )}

        {actionSuccessMsg && (
          <div className="alert alert-success" style={{ marginBottom: '0.75rem' }}>
            <span>✓</span><span>{actionSuccessMsg}</span>
          </div>
        )}
        {actionErr && (
          <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>
            <span>⚠</span><span>{actionErr}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.625rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }}
            disabled={actionMutation.isPending || needsCheck} onClick={handleSubmit}>
            {actionMutation.isPending ? 'Processing…' : 'Submit Action'}
          </button>
          <button className="btn btn-ghost" onClick={() => navigate('/admin/applications')}>Cancel</button>
        </div>
      </Section>
    );
  };

  const CheckerPanel = () => {
    if (!isChecker) return null;

    if (checkerDone) {
      return (
        <Section title="Decision Recorded">
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✓</div>
            <h2 style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: '0.375rem', fontSize: '1.05rem' }}>
              Decision submitted
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
              <button className="btn btn-primary" style={{ fontSize: '0.82rem' }} onClick={() => navigate('/admin/approvals')}>
                Back to Approval Queue
              </button>
              <button className="btn btn-ghost" style={{ fontSize: '0.82rem' }} onClick={() => navigate('/admin/applications')}>
                All Applications
              </button>
            </div>
          </div>
        </Section>
      );
    }

    if (!pendingApproval) {
      return (
        <Section title="Checker Decision">
          <div className="alert alert-info">
            <span>ℹ</span>
            <span style={{ fontSize: '0.85rem' }}>No pending maker recommendation for this application.</span>
          </div>
        </Section>
      );
    }

    const nd = pendingApproval.new_data || {};
    const sanctioned = parseFloat(nd.sanctioned_amount || loan?.requested_amount || 0);
    const approved = parseFloat(nd.approved_amount || sanctioned);
    const reduced = sanctioned - approved > 0.01;

    return (
      <Section title="Checker Decision" subtitle={pendingApproval.reference_number}>
        {/* Maker recommendation summary */}
        <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.7rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
            Maker Recommendation
          </div>
          <InfoRow label="Maker" value={pendingApproval.maker_email} />
          <InfoRow label="Action" value={humanize(pendingApproval.action?.replace('loan.', ''))} />
          {pendingApproval.action === 'loan.approve' && (
            <>
              <InfoRow label="Sanctioned" value={fc(sanctioned)} />
              <InfoRow
                label="Approved"
                value={<>{fc(approved)}{reduced && <span style={{ color: '#ff9900', fontSize: '0.7rem', marginLeft: '0.4rem' }}>(↓ {fc(sanctioned - approved)} reduced)</span>}</>}
                color={reduced ? '#ff9900' : '#00C896'}
              />
              {nd.interest_rate && <InfoRow label="Rate" value={`${nd.interest_rate}% p.m.`} />}
              {nd.tenure_months && <InfoRow label="Tenure" value={`${nd.tenure_months} months`} />}
            </>
          )}
          {pendingApproval.description && (
            <p style={{ marginTop: '0.5rem', color: 'var(--text-2)', fontSize: '0.78rem' }}>{pendingApproval.description}</p>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Decision</label>
          <select className="form-input" value={checkerDecision} onChange={e => { setCheckerDecision(e.target.value); setCheckerErr(''); }}>
            <option value="approved">Approve</option>
            <option value="rejected">Reject</option>
            <option value="escalated">Escalate</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Comment {checkerDecision === 'rejected' ? '(required)' : '(optional)'}</label>
          <textarea className="form-input" rows={3} value={checkerComment}
            onChange={e => setCheckerComment(e.target.value)} placeholder="Add your review comment…" />
        </div>

        {checkerErr && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{checkerErr}</span></div>}

        <div style={{ display: 'flex', gap: '0.625rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }}
            disabled={decideMutation.isPending}
            onClick={() => decideMutation.mutate({ decision: checkerDecision, comment: checkerComment })}>
            {decideMutation.isPending ? 'Submitting…' : 'Submit Decision'}
          </button>
          <button className="btn btn-ghost" onClick={() => navigate('/admin/approvals')}>Cancel</button>
        </div>
      </Section>
    );
  };

  // ── Page ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 1140, margin: '0 auto' }}>

      {/* Back nav + header */}
      <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }} onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: '1.3rem' }}>Application Review</h1>
          <LoanStatusBadge status={loan.status} />
          <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--text-3)' }}>
            {loan.application_number}
          </span>
        </div>
        {!canReview && (
          <div style={{ fontSize: '0.75rem', padding: '0.4rem 0.875rem', background: 'rgba(91,142,255,0.07)', border: '1px solid rgba(91,142,255,0.25)', borderRadius: 'var(--radius)', color: 'var(--text-2)' }}>
            Read-only
          </div>
        )}
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '1.25rem', alignItems: 'start' }}>

        {/* ── LEFT: Application info + Credit check + Documents ── */}
        <div>

          {/* Application Overview */}
          <Section title="Application Overview">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {/* Applicant */}
              <div>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                  Applicant
                </div>
                <InfoRow label="Name"  value={loan.applicant_name} />
                <InfoRow label="Email" value={loan.applicant_email} mono />
                <InfoRow label="Tier"  value={loan.applicant_tier ? `Tier ${loan.applicant_tier}` : undefined} />
                <InfoRow
                  label="KYC"
                  value={humanize(loan.kyc_status || '')}
                  color={kycColor(loan.kyc_status)}
                />
                {loan.reviewed_by_email && (
                  <InfoRow label="Reviewed by" value={loan.reviewed_by_email} mono />
                )}
              </div>
              {/* Loan details */}
              <div>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                  Loan Details
                </div>
                <InfoRow label="Product"    value={loan.product_name || humanize(loan.loan_type)} />
                <InfoRow label="Requested"  value={fc(loan.requested_amount)} color="var(--text-1)" />
                {loan.approved_amount && (
                  <InfoRow label="Approved" value={fc(loan.approved_amount)} color="#00C896" />
                )}
                <InfoRow label="Tenure"     value={loan.requested_tenure_months ? `${loan.requested_tenure_months} months` : undefined} />
                <InfoRow label="Rate"       value={loan.applied_interest_rate ? `${loan.applied_interest_rate}% p.m.` : undefined} />
                <InfoRow label="Purpose"    value={humanize(loan.purpose || '')} />
                <InfoRow label="Submitted"  value={formatDate(loan.submitted_at || loan.created_at)} />
              </div>
            </div>

            {/* Notes & rejection reason */}
            {loan.rejection_reason && (
              <div style={{ marginTop: '0.875rem', padding: '0.5rem 0.75rem', background: 'rgba(255,71,87,0.06)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: 8, fontSize: '0.8rem' }}>
                <span style={{ fontWeight: 600, color: '#FF4757' }}>Rejection reason: </span>
                <span style={{ color: 'var(--text-2)' }}>{loan.rejection_reason}</span>
              </div>
            )}
            {loan.notes && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.8rem' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>Notes: </span>
                <span style={{ color: 'var(--text-2)' }}>{loan.notes}</span>
              </div>
            )}
          </Section>

          {/* Bureau Credit Check */}
          {(() => {
            const isPostAcceptance = ['offer_accepted', 'disbursed', 'active', 'closed'].includes(loan.status);
            const activeResult = activeTab === 'nano' ? nanoResult : classicResult;
            const tabs = [
              { key: 'nano',    label: 'Nano',    result: nanoResult,    sub: 'BVN summary' },
              { key: 'classic', label: 'Classic', result: classicResult, sub: 'Full institution report' },
            ];
            return (
              <Section
                title="Bureau Credit Check"
                subtitle={canReview && !isPostAcceptance ? 'Required before approve / reject' : undefined}
              >
                {/* Tab strip */}
                <div style={{ display: 'flex', gap: 0, marginBottom: '1.25rem', borderBottom: '1px solid var(--border)' }}>
                  {tabs.map(t => (
                    <button key={t.key} onClick={() => { setActiveTab(t.key); setCheckErr(''); }} style={{
                      padding: '0.45rem 1.25rem', fontSize: '0.82rem', fontWeight: activeTab === t.key ? 700 : 400,
                      background: 'none', border: 'none', cursor: 'pointer',
                      borderBottom: activeTab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
                      color: activeTab === t.key ? 'var(--primary)' : 'var(--text-2)',
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                    }}>
                      {t.label}
                      {t.result ? (
                        <span style={{
                          fontSize: '0.62rem', fontWeight: 700, padding: '0.1rem 0.45rem', borderRadius: 99,
                          background: t.result.recommendation === 'good' ? 'rgba(0,200,150,0.15)' : t.result.recommendation === 'bad' ? 'rgba(255,71,87,0.15)' : 'rgba(255,153,0,0.15)',
                          color: t.result.recommendation === 'good' ? '#00C896' : t.result.recommendation === 'bad' ? '#FF4757' : '#ff9900',
                        }}>
                          {t.result.recommendation === 'no_hit' ? 'NO HIT' : t.result.recommendation?.toUpperCase()}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.62rem', color: 'var(--text-3)', padding: '0.1rem 0.45rem', borderRadius: 99, background: 'var(--surface-alt)' }}>
                          Not run
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {activeResult ? (
                  <>
                    <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <RecommendationBadge value={activeResult.recommendation} />
                      <span><span style={{ color: 'var(--text-3)' }}>Checked: </span><strong>{formatDate(activeResult.checked_at)}</strong></span>
                      {activeResult.checked_by_name && (
                        <span><span style={{ color: 'var(--text-3)' }}>By: </span><strong>{activeResult.checked_by_name}</strong></span>
                      )}
                    </div>

                    {activeResult.summary?.hit_type === 'no_hit' ? (
                      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
                        <span>ℹ</span>
                        <span style={{ fontSize: '0.85rem' }}>No credit history found for this customer in the bureau.</span>
                      </div>
                    ) : activeTab === 'nano' ? (
                      <NanoSummary summary={activeResult.summary} />
                    ) : (
                      <ClassicSummary summary={activeResult.summary} />
                    )}

                    {canReview && !isPostAcceptance && (
                      <div style={{ marginTop: '1rem', paddingTop: '0.875rem', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-3)', flex: 1 }}>
                          Re-run <strong style={{ textTransform: 'capitalize' }}>{activeTab}</strong> check to refresh
                        </span>
                        <button className="btn btn-ghost" style={{ fontSize: '0.78rem', padding: '0.3rem 0.875rem' }}
                          disabled={runCheck.isPending} onClick={() => runCheck.mutate()}>
                          {runCheck.isPending ? 'Running…' : '↻ Re-run Check'}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  canReview && !isPostAcceptance ? (
                    <>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '1.25rem' }}>
                        No <strong style={{ textTransform: 'capitalize' }}>{activeTab}</strong> check has been run yet.{' '}
                        {tabs.find(t => t.key !== activeTab)?.result
                          ? `Switch to the ${activeTab === 'nano' ? 'Classic' : 'Nano'} tab to view existing results.`
                          : 'Run a check before approving or rejecting.'
                        }
                      </p>
                      <button className="btn btn-primary" disabled={runCheck.isPending} onClick={() => runCheck.mutate()}>
                        {runCheck.isPending ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin .6s linear infinite', display: 'inline-block' }} />
                            Running…
                          </span>
                        ) : `Run ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Check`}
                      </button>
                    </>
                  ) : (
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-3)', fontStyle: 'italic' }}>
                      No {activeTab} credit check has been run for this application yet.
                    </p>
                  )
                )}

                {checkErr && (
                  <div className="alert alert-error" style={{ marginTop: '1rem' }}>
                    <span>⚠</span><span>{checkErr}</span>
                  </div>
                )}
              </Section>
            );
          })()}

          {/* Document Requests */}
          {canReview && (
            <Section title="Documents" subtitle="Requested documents and applicant uploads">
              {docRequests.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-3)', fontStyle: 'italic' }}>
                  No document requests yet. Use "Request Additional Documents" in the action panel.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                  {docRequests.map(req => (
                    <DocumentRequestRow key={req.id} req={req} reviewDocMutation={reviewDocMutation} waiveMutation={waiveMutation} />
                  ))}
                </div>
              )}
            </Section>
          )}
        </div>

        {/* ── RIGHT: Action / Decision panel (sticky) ── */}
        <div style={{ position: 'sticky', top: '1.5rem', maxHeight: 'calc(100vh - 4rem)', overflowY: 'auto' }}>
          <ActionPanel />
          <CheckerPanel />
          {!isMaker && !isChecker && (
            <Section title="Actions">
              <div className="alert alert-info">
                <span>ℹ</span>
                <span style={{ fontSize: '0.85rem' }}>Read-only — no permissions to action this application.</span>
              </div>
            </Section>
          )}
        </div>

      </div>
    </div>
  );
}
